/**
 * Where an application sits on the Bewerbung rail (COL-533).
 *
 * The design's applicant frame shows a four-step rail per application —
 * Submitted · In review · Offer · Contract. Nothing in the schema carries
 * those four values: `bookings.status` has no `applied` and no `rejected` (see
 * the check constraint in `20260802110000_widen_bookings_status_check_dev_parity`),
 * and the application form writes plain `pending`. So the rail is DERIVED, and
 * it is derived from the same two predicates the journey ladder uses rather
 * than from a fresh reading of `contract_status`:
 *
 *   Submitted   the application exists, nothing has happened to it yet
 *   In review   an admin has acted on it (approved / confirmed), no contract yet
 *   Offer       a contract has been released for signature
 *   Contract    a signed copy is on file
 *
 * `isContractSentForSignature` and `isContractSignedForJourney` both enforce
 * the golden rule — no document on file, no contract stage, whatever the
 * status column claims — so an application cannot show "Offer" because someone
 * set a status by hand.
 */

import {
  isContractSentForSignature,
  isContractSignedForJourney,
} from "@/lib/tenantJourney";

export const APPLICATION_STEPS = [
  "submitted",
  "in_review",
  "offer",
  "contract",
] as const;

export type ApplicationStep = (typeof APPLICATION_STEPS)[number];

/** Statuses that mean an admin has picked the application up. */
const REVIEWED_STATUSES = ["approved", "confirmed", "active"] as const;

/**
 * Statuses that END an application rather than advancing it.
 *
 * `cancelled` is how a withdrawal or a rejection is recorded — there is no
 * `rejected` status — and `archived`/`moved_out`/`terminated` are past
 * tenancies. None of them belong in a list headed "Your applications".
 */
export const CLOSED_APPLICATION_STATUSES = [
  "cancelled",
  "archived",
  "moved_out",
  "terminated",
] as const;

export function isClosedApplication(
  booking: {
    status?: unknown;
  } | null
): boolean {
  const status = String(booking?.status ?? "").toLowerCase();
  return (CLOSED_APPLICATION_STATUSES as readonly string[]).includes(status);
}

export function applicationStep(booking: any): ApplicationStep {
  if (isContractSignedForJourney(booking)) return "contract";
  if (isContractSentForSignature(booking)) return "offer";
  const status = String(booking?.status ?? "").toLowerCase();
  if ((REVIEWED_STATUSES as readonly string[]).includes(status)) {
    return "in_review";
  }
  return "submitted";
}

/** 0-based index, for rendering the rail's filled segments. */
export function applicationStepIndex(booking: any): number {
  return APPLICATION_STEPS.indexOf(applicationStep(booking));
}

/**
 * Is this booking still an APPLICATION rather than a tenancy?
 *
 * The distinction the V2 tab model needs. A booking whose contract has been
 * released for signature is a tenancy in progress — the tenant needs the room
 * page, the contract and the deposit. Before that there is nothing on those
 * tabs, and the applicant frame (rail, documents, keep browsing) is the
 * screen that actually serves them.
 */
export function isApplicationNotTenancy(booking: any): boolean {
  if (!booking) return false;
  if (isClosedApplication(booking)) return false;
  return !isContractSentForSignature(booking);
}
